/**
 * Ember Admin — AntD 主题 · 视觉语言 2.0「炉火 Ember Hearth」草案
 * 真相源: docs/design/language-2.0/SPEC.md §⑦
 * -----------------------------------------------------------------------------
 * 用法 (替换 App.tsx 内联 theme 对象):
 *   import { emberHearthTheme } from './theme/antd-theme';
 *   <ConfigProvider locale={zhCN} button={{ autoInsertSpace: false }} theme={emberHearthTheme}>
 *
 * 红线对齐:
 * - colorLink 用 eb-700 文字档 #AC3A0E (eb-500/600 小字不达 4.5:1, RL-8)
 * - 阴影全部暖炭基 #2E2620, 禁冷黑 (RL-9)
 * - 火光影只给主按钮 (Button.primaryShadow), 不进通用 boxShadow (RL-3)
 */
import type { ThemeConfig } from 'antd';

/** 炉火核心色值 (与 tokens.wxss / owner-dashboard-tokens.css 同源) */
export const hearth = {
  /* 火心橙 Ember */
  ember50: '#FFF6EE',
  ember100: '#FFE8D7',
  ember200: '#FFD0B0',
  ember400: '#FB8348',
  ember500: '#F05D23',
  ember600: '#BC400F', // 文字/大字通用档 (白底 5.41:1; eb-600 加深一档过 RL-8)
  ember700: '#AC3A0E', // 链接/强调小字档 (6.2:1)
  /* 暖纸 / 炭墨 */
  paper0: '#FFFFFF',
  paper1: '#FBF8F4',
  paper2: '#F4EEE6',
  line1: '#EAE2D7',
  line2: '#D9CEBF',
  ink1: '#2E2620',
  ink2: '#5A4F42',
  ink3: '#736858', // B 原值 #7D7264 微调深以过 RL-8 (paper-1 5.15 / paper-2 4.73)
  ink4: '#A39684',
  charDeep: '#1E1813',
  /* 语义 (500 图形档 / 600 文字档) */
  up: '#2E9E5B',
  upText: '#237A46',
  upBg: '#E8F5EC',
  down: '#D6453D',
  downText: '#B93A33',
  downBg: '#FBEAE8',
  warn: '#D98E04',
  warnText: '#956102',
  warnBg: '#FCF3DF',
  info: '#3E7DBF',
  infoText: '#2E6094',
  infoBg: '#E9F1F9',
  /* dataviz 8 色定编 (SPEC §④; dv1=本期/尹三顺, dv2=俏江溪) */
  dv: ['#F05D23', '#2E8B9A', '#E3A008', '#4072B8', '#C24A66', '#5F9E4E', '#8C6FBE', '#8F7355'],
} as const;

export const emberHearthTheme: ThemeConfig = {
  // algorithm: theme.defaultAlgorithm 由调用侧保留 (不在此 import antd 运行时)
  token: {
    /* —— 色彩 —— */
    colorPrimary: hearth.ember500, // #F05D23 火心橙
    colorSuccess: hearth.up, // #2E9E5B 盈绿
    colorWarning: hearth.warn, // #D98E04 琥珀
    colorError: hearth.down, // #D6453D 椒红
    colorInfo: hearth.info, // #3E7DBF 黛蓝
    colorLink: hearth.ember700, // #AC3A0E 链接文字档 (RL-8)

    /* —— 文字四阶 (炭墨) —— */
    colorText: hearth.ink1,
    colorTextSecondary: hearth.ink2,
    colorTextTertiary: hearth.ink3,
    colorTextQuaternary: hearth.ink4,

    /* —— 面 / 线 (暖纸) —— */
    colorBgLayout: hearth.paper1, // #FBF8F4 页底
    colorBgContainer: hearth.paper0, // 卡面
    colorBgElevated: hearth.paper0,
    colorBorder: hearth.line2, // #D9CEBF 输入框等可见描边
    colorBorderSecondary: hearth.line1, // #EAE2D7 卡片/分隔
    colorSplit: '#F0EAE0',
    colorFillTertiary: hearth.paper2, // 下沉填充
    colorFillQuaternary: '#F8F3EC',

    /* —— 形 (B 四档: xs6 sm10 md14 lg20) —— */
    borderRadius: 10, // 按钮/输入 = sm
    borderRadiusLG: 14, // 卡片 = md
    borderRadiusSM: 6, // 标签/角标 = xs

    /* —— 暖炭影 (RL-9; 三级制 sh-2/sh-3) —— */
    boxShadow:
      '0 2px 4px rgba(46, 38, 32, 0.06), 0 12px 32px rgba(46, 38, 32, 0.09)',
    boxShadowSecondary: '0 10px 28px rgba(46, 38, 32, 0.14)',

    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif",
  },
  components: {
    Card: {
      borderRadiusLG: 14,
    },
    Table: {
      headerBg: '#F7F2EA', // 暖纸表头
      rowHoverBg: hearth.ember50, // #FFF6EE 灭冷蓝 hover
      borderColor: '#F0EAE0',
      headerColor: hearth.ink2,
      rowSelectedBg: hearth.ember50, // #FFF6EE 取代 antd 蓝底选中行 (§11.3 死角)
      rowSelectedHoverBg: hearth.ember100, // #FFE8D7
      bodySortBg: hearth.paper1, // #FBF8F4 排序激活列暖底 (灭默认冷灰 #fafafa)
    },
    Button: {
      // 火光影仅主按钮 (G3 特权彩影, 收敛版 alpha .25)
      primaryShadow: '0 4px 12px rgba(240, 93, 35, 0.25)',
      defaultBorderColor: hearth.line2,
      defaultColor: hearth.ink2,
    },
    Tabs: {
      inkBarColor: hearth.ember500,
      itemSelectedColor: hearth.ember700,
      itemHoverColor: hearth.ember600,
    },
    Menu: {
      itemSelectedBg: hearth.ember50,
      itemSelectedColor: hearth.ember700,
      itemActiveBg: hearth.ember50, // hover/active 暖底, 灭冷蓝
    },
    // —— §11.3 冷蓝死角统一收口 (focus ring / 下拉选中 / 分页 / 分段 / 单选) ——
    Input: {
      activeBorderColor: hearth.ember400, // #FB8348 focus 描边火橙 (灭冷蓝 focus ring)
      hoverBorderColor: hearth.ember400,
      activeShadow: '0 0 0 2px rgba(240, 93, 35, 0.12)', // 火橙 focus 光晕
    },
    Select: {
      optionSelectedBg: hearth.ember50, // #FFF6EE 选项选中暖底
      optionActiveBg: hearth.paper1, // #FBF8F4 hover
    },
    Pagination: {
      itemActiveBg: hearth.ember50, // 当前页暖底 (灭冷蓝)
    },
    Segmented: {
      itemSelectedBg: hearth.ink1, // #2E2620 炭墨选中 (§8, 非蓝)
      itemSelectedColor: 'rgba(255, 246, 238, 0.96)', // 暖白字
    },
    Radio: {
      buttonSolidCheckedBg: hearth.ember500, // #F05D23
      buttonSolidCheckedHoverBg: hearth.ember600, // #BC400F
    },
    Statistic: {
      // 数字纪律: tabular-nums 由全局样式开启 (font-variant-numeric), 此处仅字色
      contentFontSize: 24, // B num-lg
    },
    Tag: {
      defaultBg: hearth.paper2,
      defaultColor: hearth.ink2,
    },
  },
};
